/**
 * Audio Mixer Utility
 * 
 * Handles mixing multiple audio sources using Web Audio API
 * with configurable gain levels for each source.
 */

export interface AudioMixerConfig {
    microphoneStream: MediaStream;
    systemStream: MediaStream;
    microphoneGain?: number; // 0-1, default 0.5
    systemGain?: number; // 0-1, default 0.5
}

export interface MixRatio {
    microphone: number; // 0-1
    system: number; // 0-1
}

export class AudioMixer {
    private audioContext: AudioContext | null = null;
    private microphoneSource: MediaStreamAudioSourceNode | null = null;
    private systemSource: MediaStreamAudioSourceNode | null = null;
    private microphoneGain: GainNode | null = null;
    private systemGain: GainNode | null = null;
    private destination: MediaStreamAudioDestinationNode | null = null;
    private mixedStream: MediaStream | null = null;
    private microphoneStream: MediaStream | null = null;
    private systemStream: MediaStream | null = null;

    /**
     * Initialize the audio mixer with two input streams
     */
    async initialize(config: AudioMixerConfig): Promise<MediaStream> {
        try {
            // Store references to original streams for cleanup
            this.microphoneStream = config.microphoneStream;
            this.systemStream = config.systemStream;

            // Create audio context with optimal sample rate for WebRTC
            this.audioContext = new AudioContext({ sampleRate: 48000 });

            // Create source nodes from input streams
            this.microphoneSource = this.audioContext.createMediaStreamSource(config.microphoneStream);
            this.systemSource = this.audioContext.createMediaStreamSource(config.systemStream);

            // Create gain nodes for mixing
            this.microphoneGain = this.audioContext.createGain();
            this.systemGain = this.audioContext.createGain();

            // Set initial gain values
            this.microphoneGain.gain.value = config.microphoneGain ?? 0.5;
            this.systemGain.gain.value = config.systemGain ?? 0.5;

            // Create destination node
            this.destination = this.audioContext.createMediaStreamDestination();

            // Connect the audio graph
            this.microphoneSource.connect(this.microphoneGain);
            this.systemSource.connect(this.systemGain);
            this.microphoneGain.connect(this.destination);
            this.systemGain.connect(this.destination);

            // Get the mixed output stream
            this.mixedStream = this.destination.stream;

            return this.mixedStream;
        } catch (error) {
            this.cleanup();
            throw new Error(`Failed to initialize audio mixer: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Update the mix ratio between microphone and system audio
     */
    setMixRatio(ratio: MixRatio): void {
        if (!this.microphoneGain || !this.systemGain) {
            throw new Error('Audio mixer not initialized');
        }

        // Validate ratio values
        if (ratio.microphone < 0 || ratio.microphone > 1) {
            throw new Error('Microphone gain must be between 0 and 1');
        }
        if (ratio.system < 0 || ratio.system > 1) {
            throw new Error('System gain must be between 0 and 1');
        }

        // Update gain values
        this.microphoneGain.gain.value = ratio.microphone;
        this.systemGain.gain.value = ratio.system;
    }

    /**
     * Get current mix ratio
     */
    getMixRatio(): MixRatio | null {
        if (!this.microphoneGain || !this.systemGain) {
            return null;
        }

        return {
            microphone: this.microphoneGain.gain.value,
            system: this.systemGain.gain.value,
        };
    }

    /**
     * Get the mixed output stream
     */
    getMixedStream(): MediaStream | null {
        return this.mixedStream;
    }

    /**
     * Check if mixer is initialized
     */
    isInitialized(): boolean {
        return this.audioContext !== null && this.mixedStream !== null;
    }

    /**
     * Clean up resources and stop all source streams
     */
    cleanup(): void {
        // Stop all tracks from original source streams
        if (this.microphoneStream) {
            this.microphoneStream.getTracks().forEach(track => {
                track.stop();
                console.log('Stopped microphone track:', track.kind);
            });
            this.microphoneStream = null;
        }

        if (this.systemStream) {
            this.systemStream.getTracks().forEach(track => {
                track.stop();
                console.log('Stopped system audio track:', track.kind);
            });
            this.systemStream = null;
        }

        // Disconnect audio nodes
        if (this.microphoneSource) {
            this.microphoneSource.disconnect();
            this.microphoneSource = null;
        }
        if (this.systemSource) {
            this.systemSource.disconnect();
            this.systemSource = null;
        }
        if (this.microphoneGain) {
            this.microphoneGain.disconnect();
            this.microphoneGain = null;
        }
        if (this.systemGain) {
            this.systemGain.disconnect();
            this.systemGain = null;
        }
        if (this.destination) {
            this.destination.disconnect();
            this.destination = null;
        }

        // Close audio context
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = null;
        }

        this.mixedStream = null;
    }
}
